/**
 * useAiAssist -- Generic hook for AI-assisted form fields.
 *
 * Manages the full AI request lifecycle: idle, loading, suggestion ready,
 * and error states. Prevents double-submission and provides accept/dismiss
 * actions for the suggestion panel.
 *
 * @see ADR-055 for AI-assisted resume feature architecture.
 */
import { useState, useCallback, useRef } from 'react';
import type { AiResponse } from '../types/ai.types';

interface UseAiAssistOptions<TInput, TData> {
  /** The API call that returns an AiResponse discriminated union. */
  apiCall: (input: TInput) => Promise<AiResponse<TData>>;
  /** Called when the user accepts the suggestion. */
  onAccept?: (data: TData) => void;
}

interface UseAiAssistState<TInput, TData> {
  /** The current AI suggestion, or null when idle/loading/error. */
  suggestion: TData | null;
  /** Whether an AI request is in flight. */
  isLoading: boolean;
  /** Error message from the AI service, or null. */
  error: string | null;
  /** Fire the AI request. No-op if already loading. */
  trigger: (input: TInput) => Promise<void>;
  /** Accept the suggestion, invoke onAccept callback, and reset state. */
  accept: () => void;
  /** Dismiss the suggestion without accepting. */
  dismiss: () => void;
}

/**
 * Hook that manages the AI suggestion lifecycle for a single form field.
 *
 * State machine:
 *   Idle    -> Loading (trigger)
 *   Loading -> Suggestion (success) | Error (failure) | Idle (dismiss while loading is no-op)
 *   Suggestion -> Idle (accept or dismiss)
 *   Error   -> Idle (dismiss) | Loading (retry via trigger)
 */
export function useAiAssist<TInput, TData>(
  options: UseAiAssistOptions<TInput, TData>,
): UseAiAssistState<TInput, TData> {
  const [suggestion, setSuggestion] = useState<TData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to prevent double-submit while a request is in flight.
  const loadingRef = useRef(false);

  // Keep options in a ref to avoid stale closures without adding to deps.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const trigger = useCallback(async (input: TInput) => {
    // Prevent double-submit.
    if (loadingRef.current) return;

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);
    setSuggestion(null);

    const result = await optionsRef.current.apiCall(input);

    loadingRef.current = false;
    setIsLoading(false);

    if (result.error) {
      setError(result.error);
    } else {
      setSuggestion(result.data ?? null);
    }
  }, []);

  const accept = useCallback(() => {
    if (suggestion !== null) {
      optionsRef.current.onAccept?.(suggestion);
    }
    setSuggestion(null);
    setError(null);
  }, [suggestion]);

  const dismiss = useCallback(() => {
    setSuggestion(null);
    setError(null);
  }, []);

  return { suggestion, isLoading, error, trigger, accept, dismiss };
}
